import findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {compose} from 'redux';
import moment from 'moment';
import Arg from '@spothero/arg';
import StorageUtils from '@spothero/utils/storage';
import GTMUtils from 'utils/gtm';
import SHUtils from 'utils/sh-utils';
import withConfig from 'config/withConfig';
import {getSpots, getSelectedSpotById} from 'spots/spots-selectors';
import {setSelectedSpotId} from 'spots/spots-actions';
import {updateSelected} from 'spot/spot-actions';
import SearchUtils from 'search/search-utils';
import {Page} from 'page/utils';
import {push, replace} from 'router/router-actions';
import trackSearchLanding from 'segment/pages/search-landing';
import {routeToSearch} from 'router/router-utils';
import {setSortType} from './search-actions';
import SearchMeta from './SearchMeta';
import {Helmet} from 'react-helmet-async';
import classNames from 'classnames';

export default EnhancedComponent => {
    class WithSearch extends Component {
        static propTypes = {
            history: PropTypes.shape({
                location: PropTypes.object,
            }),
            match: PropTypes.shape({
                params: PropTypes.shape({
                    page: PropTypes.string,
                    section: PropTypes.string,
                }),
            }).isRequired,
            searchType: PropTypes.string.isRequired,
            siteUrl: PropTypes.string.isRequired,
            isMobile: PropTypes.bool.isRequired,
            isMonthly: PropTypes.bool.isRequired,
            starts: PropTypes.string,
            ends: PropTypes.string,
            cityId: PropTypes.number.isRequired,
            citySlug: PropTypes.string.isRequired,
            cityMonthlyRelativeUrl: PropTypes.string,
            cityRelativeUrl: PropTypes.string,
            event: PropTypes.object,
            destination: PropTypes.shape(DESTINATION_DATA).isRequired,
            spots: PropTypes.arrayOf(PropTypes.object).isRequired,
            querySpotId: PropTypes.number,
            searchRequestId: PropTypes.string,
            pageType: PropTypes.string,
            searchUrl: PropTypes.string,
            updateSelectedSpotId: PropTypes.func.isRequired,
            idealDistance: PropTypes.number,
            isFiltered: PropTypes.bool,
            latitude: PropTypes.number.isRequired,
            longitude: PropTypes.number.isRequired,
            referrerAffiliate: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            sortBy: PropTypes.string.isRequired,
            routePush: PropTypes.func.isRequired,
            routeReplace: PropTypes.func.isRequired,
            onChangeSort: PropTypes.func.isRequired,
        };

        constructor(props) {
            super(props);

            const {isMobile, searchType, destination} = props;
            const isVenue = searchType === 'venue';
            const isEventListEnabled = Boolean(
                isVenue &&
                    destination.has_upcoming_events &&
                    !destination.hide_event_modal
            );
            const {
                isLowInventoryEnabled,
                isSoldOutEnabled,
            } = SearchUtils.getSocialProofExperimentSettings();

            this._isLowInventoryEnabled = isLowInventoryEnabled;
            this._isSoldOutEnabled = isSoldOutEnabled;

            this.state = {
                isEventListEnabled,
                showEventList: isMobile ? false : isEventListEnabled,
                bodyClasses: SHUtils.determineInitialLoadBodyClasses({
                    isSearchPage: true,
                    searchType,
                }),
            };
        }

        static getDerivedStateFromProps(nextProps, prevState) {
            const {searchType, destination} = nextProps;
            const {isEventListEnabled: prevIsEventListEnabled} = prevState;

            const bodyClasses = classNames(
                SHUtils.determineInitialLoadBodyClasses({
                    isSearchPage: true,
                    searchType,
                })
            );

            const isEventListEnabled =
                searchType === 'venue' &&
                destination.has_upcoming_events &&
                !destination.hide_event_modal
                    ? true
                    : searchType !== 'venue' ||
                      !destination.has_upcoming_events ||
                      destination.hide_event_modal
                    ? false
                    : prevIsEventListEnabled;

            return {bodyClasses, isEventListEnabled};
        }

        componentDidMount() {
            const {referrerAffiliate} = this.props;

            StorageUtils.set('searchStartTime', moment().format());

            trackSearchLanding(referrerAffiliate);

            this._trackPageView();
        }

        componentDidUpdate(prevProps) {
            const {
                searchRequestId,
                querySpotId,
                updateSelectedSpotId,
            } = this.props;
            const {
                searchRequestId: prevSearchRequestId,
                querySpotId: prevQuerySpotId,
            } = prevProps;

            if (searchRequestId !== prevSearchRequestId) {
                this._trackPageView();
            }

            if (prevQuerySpotId && !querySpotId) {
                // spot-id was removed from URL, close spot details
                updateSelectedSpotId({
                    spotId: null,
                });
            } else if (prevQuerySpotId !== querySpotId) {
                // new spot-id was detected in URL, open that one instead
                updateSelectedSpotId({
                    spotId: querySpotId,
                });
            }
        }

        componentWillUnmount() {
            if (this._rateLoader) {
                this._rateLoader.cancel();
                this._rateLoader = null;
            }
        }

        _onToggleEventList = () => {
            this.setState(prevState => ({
                showEventList: !prevState.showEventList,
            }));
        };

        _onSpotDetailsHidden = () => {
            const {
                history: {
                    location: {pathname, search},
                },
                routePush,
            } = this.props;
            const queryParams = Arg.parse(search);

            delete queryParams['spot-id'];

            routePush(`${pathname}?${Arg.stringify(queryParams)}`);
        };

        _onShowDetailsClick = spotId => {
            const {spots, sortBy} = this.props;

            StorageUtils.set(SearchUtils.SEGMENT_SPOT_META, {
                rank: findIndex(spots, {spotId}) + 1,
                sortBy,
            });
        };

        _trackPageView() {
            const {searchType} = this.props;

            GTMUtils.push({
                event: 'pageview',
                page: searchType === 'city' ? 'city' : 'search',
            });
        }

        _resetToDefaultRoute() {
            const {
                isMonthly,
                pageType,
                latitude,
                longitude,
                routeReplace,
                cityId,
                destination,
            } = this.props;
            const id =
                pageType === Page.CITY
                    ? cityId
                    : pageType === Page.DESTINATION || pageType === Page.VENUE
                    ? destination.id
                    : null;

            routeToSearch({
                method: routeReplace,
                pageType,
                id,
                queryParams: {
                    ...(isMonthly && {monthly: true}),
                    ...(!id && {
                        latitude,
                        longitude,
                    }),
                },
            });
        }

        render() {
            const {
                history,
                sortBy,
                searchType,
                searchUrl,
                isMonthly,
                starts,
                ends,
                spots,
                siteUrl,
                citySlug,
                cityMonthlyRelativeUrl,
                cityRelativeUrl,
                destination,
                event,
                querySpotId,
                routePush,
                idealDistance,
                isFiltered,
                onChangeSort,
            } = this.props;
            const {isEventListEnabled, showEventList, bodyClasses} = this.state;
            const hasDestination = !isEmpty(destination);
            // This is redundant with the canonicalUrl calculation in SearchMeta
            // We will be address this with updates to meta content in CONWEB-483
            const canonicalUrl =
                searchType === 'event' && !isEmpty(event)
                    ? `${siteUrl}/${event.search_url}`
                    : hasDestination
                    ? `${siteUrl}/${citySlug}/${
                          isMonthly && destination.monthly_relative_url
                              ? destination.monthly_relative_url
                              : destination.relative_url
                      }?starts=${starts}&ends=${ends}`
                    : `${siteUrl}/${
                          isMonthly && cityMonthlyRelativeUrl
                              ? cityMonthlyRelativeUrl
                              : cityRelativeUrl
                      }?starts=${starts}&ends=${ends}`;

            return (
                <>
                    <Helmet>
                        <body className={bodyClasses} />
                    </Helmet>
                    <SearchMeta />
                    <EnhancedComponent
                        history={history}
                        sortBy={sortBy}
                        searchType={searchType}
                        searchUrl={searchUrl}
                        canonicalUrl={canonicalUrl}
                        isMonthly={isMonthly}
                        starts={starts}
                        ends={ends}
                        spots={spots}
                        selectedSpotId={querySpotId}
                        pushTo={routePush}
                        findParkingNearMe={this._findParkingNearMe}
                        onSpotDetailsHidden={this._onSpotDetailsHidden}
                        idealDistance={idealDistance}
                        isEventListEnabled={isEventListEnabled}
                        showEventList={showEventList}
                        isFiltered={isFiltered}
                        onChangeSort={onChangeSort}
                        onToggleEventList={this._onToggleEventList}
                        isLowInventoryEnabled={this._isLowInventoryEnabled}
                        isSoldOutEnabled={this._isSoldOutEnabled}
                        onShowDetailsClick={this._onShowDetailsClick}
                    />
                </>
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const {
            city: {
                data: {
                    id: cityId,
                    slug: citySlug,
                    timezone,
                    monthly_relative_url: cityMonthlyRelativeUrl,
                    relative_url: cityRelativeUrl,
                },
            },
            destination: {data: destination},
            event: {data: event},
            filters: {numActive},
            search: {
                data: {searchType, referrerAffiliate, sortBy},
            },
            searchRequest: {
                monthly: isMonthly,
                starts,
                ends,
                latitude,
                longitude,
                eid: eventId,
                ideal_search_distance: idealDistance,
                id: searchRequestId,
                pageType,
                search_url: searchUrl,
            },
        } = state;
        const {
            history: {
                location: {search: query},
            },
        } = ownProps;
        const {'spot-id': querySpotId} = Arg.parse(query);

        return {
            isMonthly,
            starts,
            ends,
            eventId,
            cityId,
            citySlug,
            cityMonthlyRelativeUrl,
            cityRelativeUrl,
            timezone,
            searchRequestId,
            pageType,
            searchUrl,
            event,
            destination,
            searchType,
            idealDistance,
            querySpotId,
            latitude,
            longitude,
            referrerAffiliate,
            sortBy,
            isFiltered: numActive > 0,
            spots: getSpots(state),
            selectedSpot: getSelectedSpotById(state),
        };
    };

    const mapDispatchToProps = {
        setSelectedSpot: updateSelected,
        updateSelectedSpotId: setSelectedSpotId,
        onChangeSort: setSortType,
        routePush: push,
        routeReplace: replace,
    };

    const enhance = compose(
        withConfig(['isMobile', 'siteUrl']),
        withRouter,
        connect(mapStateToProps, mapDispatchToProps)
    );

    return enhance(WithSearch);
};
